import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useDispatch, useSelector} from "react-redux";
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Box,
  Divider
} from '@material-ui/core';
import {connect, transceive, autoConnect, setAdminAuthenticated} from "./actions";
import {byteToHexString, hexStringToByte} from "./util";
import {useSnackbar} from "notistack";
import LockIcon from '@material-ui/icons/Lock';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  grid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(4),
    },
  },
  card: {
    margin: theme.spacing(2, 2, 3, 2), // 移动端: 16px 左右, 24px 底部
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(3, 3, 4, 3), // 平板: 24px 左右, 32px 底部
    },
    [theme.breakpoints.up('md')]: {
      marginLeft: "10%",
      marginRight: "10%",
      marginTop: "30px",
    },
  },
  slotCard: {
    border: '2px solid',
  },
  shortPressCard: {
    borderColor: theme.palette.primary.main,
  },
  longPressCard: {
    borderColor: theme.palette.secondary.main,
  },
  slotHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  slotIcon: {
    marginRight: theme.spacing(1),
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  configSection: {
    marginTop: theme.spacing(2),
  },
}));

const SLOT_TYPE = {
  OFF: 0,
  OATH: 1,
  STATIC: 2,
};

const SLOT_TYPE_NAMES = {
  0: 'Disabled',
  1: 'OATH (Dynamic)',
  2: 'Static Password',
};

// Parse TLV structure (same as in Oath.js)
function parseTLV(tlv) {
  let res = [];
  for (let i = 0; i < tlv.length;) {
    if (i + 1 >= tlv.length) {
      throw new Error('Bad TLV');
    }
    let tag = tlv[i];
    let len = tlv[i + 1];
    if (i + 1 + len >= tlv.length) {
      throw new Error('Bad TLV Length');
    }
    let value = tlv.slice(i + 2, i + 2 + len);
    i += len + 2;
    res.push({
      tag,
      len,
      value
    });
  }
  return res;
}

export default function Pass() {
  const classes = useStyles();
  const device = useSelector(state => state.device);
  const adminAuthenticated = useSelector(state => state.adminAuthenticated);
  const dispatch = useDispatch();
  const {enqueueSnackbar} = useSnackbar();

  const [shortSlot, setShortSlot] = useState({type: SLOT_TYPE.OFF, withEnter: false, password: '', oathName: ''});
  const [longSlot, setLongSlot] = useState({type: SLOT_TYPE.OFF, withEnter: false, password: '', oathName: ''});
  const [oathCredentials, setOathCredentials] = useState([]);
  
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [currentSlot, setCurrentSlot] = useState(null); // 'short' or 'long'
  const [tempConfig, setTempConfig] = useState({type: SLOT_TYPE.OFF, withEnter: false, password: '', oathName: ''});

  const selectAdminApplet = useCallback(async () => {
    if (device === null) {
      const autoConnected = await dispatch(autoConnect());
      if (!autoConnected) {
        if (!await dispatch(connect())) {
          throw new Error('Cannot connect to device');
        }
      }
    }
    
    // Retry mechanism for device busy errors
    let retries = 3;
    let lastError;
    while (retries > 0) {
      try {
        let res = await dispatch(transceive("00A4040005F000000000"));
        if (res && res.endsWith("9000")) {
          return; // Success
        }
        lastError = `Unexpected response: ${res}`;
      } catch (e) {
        lastError = e.toString();
      }
      retries--;
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    throw new Error(`Selecting admin applet failed after 3 retries: ${lastError}`);
  }, [device, dispatch]);

  const selectOathApplet = useCallback(async () => {
    // Retry mechanism for device busy errors
    let retries = 3;
    let lastError;
    while (retries > 0) {
      try {
        let res = await dispatch(transceive("00A4040007A0000005272101"));
        if (res && res.endsWith("9000")) {
          return; // Success
        }
        lastError = `Unexpected response: ${res}`;
      } catch (e) {
        lastError = e.toString();
      }
      retries--;
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    throw new Error(`Selecting OATH applet failed after 3 retries: ${lastError}`);
  }, [dispatch]);

  const loadOathCredentials = useCallback(async () => {
    try {
      await selectOathApplet();
      
      // Send LIST command (use correct format)
      let res = await dispatch(transceive("00A1000000", false, true));
      if (!res.endsWith("9000")) {
        console.warn('OATH LIST failed:', res);
        setOathCredentials([]);
        return;
      }
      
      // Parse TLV structure (same as Oath.js)
      const tlv = parseTLV(hexStringToByte(res.substring(0, res.length - 4)));
      const credentials = [];
      
      for (let i = 0; i < tlv.length; ++i) {
        if (tlv[i].tag !== 0x72) {
          console.warn('Unexpected tag in OATH LIST:', tlv[i].tag);
          continue;
        }
        
        // value[0] is type/algo byte, value[1...] is the name
        const typeAlgo = tlv[i].value[0];
        const type = (typeAlgo & 0xF0) >> 4;  // High 4 bits
        
        // PASS only supports HOTP (type=1), not TOTP (type=2)
        if (type !== 1) {
          console.log(`Skipping ${new TextDecoder("utf-8").decode(tlv[i].value.slice(1))}: PASS only supports HOTP credentials`);
          continue;
        }
        
        // For display, decode only the name part
        const displayName = new TextDecoder("utf-8").decode(tlv[i].value.slice(1));
        
        // But store the full value (with type/algo prefix) for device compatibility
        // Convert to hex string for storage
        const fullNameHex = byteToHexString(Array.from(tlv[i].value));
        
        credentials.push({
          displayName: displayName,
          fullName: fullNameHex  // Store full format for device
        });
      }
      
      console.log('Loaded OATH credentials:', credentials);
      setOathCredentials(credentials);
    } catch (e) {
      console.error('Failed to load OATH credentials:', e);
      setOathCredentials([]);
    }
  }, [dispatch, selectOathApplet]);

  const readPassConfig = useCallback(async () => {
    try {
      await selectAdminApplet();
      let res = await dispatch(transceive("0043000000"));
      
      // Check status code
      const statusCode = res.slice(-4);
      
      if (statusCode === '6982') {
        // Admin PIN not authenticated - clear state and let App.js prompt
        dispatch(setAdminAuthenticated(false));
        throw new Error('Admin authentication required. Please re-authenticate (dialog will appear automatically)');
      }
      
      if (!res.endsWith("9000")) {
        throw new Error(`Reading PASS config failed (status: ${statusCode})`);
      }
      
      const data = res.substring(0, res.length - 4);
      let offset = 0;
      
      // Parse short slot
      const shortType = parseInt(data.substring(offset, offset + 2), 16);
      offset += 2;
      let short = {type: shortType, withEnter: false, password: '', oathName: ''};
      
      if (shortType === SLOT_TYPE.STATIC) {
        const withEnter = parseInt(data.substring(offset, offset + 2), 16);
        offset += 2;
        short.withEnter = withEnter === 1;
      } else if (shortType === SLOT_TYPE.OATH) {
        const nameLen = parseInt(data.substring(offset, offset + 2), 16);
        offset += 2;
        // Read the full name (in hex format, includes type/algo prefix)
        const nameHex = data.substring(offset, offset + nameLen * 2);
        offset += nameLen * 2;
        const withEnter = parseInt(data.substring(offset, offset + 2), 16);
        offset += 2;
        short.oathName = nameHex;  // Store in hex format to match credentials list
        short.withEnter = withEnter === 1;
      }
      
      // Parse long slot
      const longType = parseInt(data.substring(offset, offset + 2), 16);
      offset += 2;
      let long = {type: longType, withEnter: false, password: '', oathName: ''};
      
      if (longType === SLOT_TYPE.STATIC) {
        const withEnter = parseInt(data.substring(offset, offset + 2), 16);
        offset += 2;
        long.withEnter = withEnter === 1;
      } else if (longType === SLOT_TYPE.OATH) {
        const nameLen = parseInt(data.substring(offset, offset + 2), 16);
        offset += 2;
        // Read the full name (in hex format, includes type/algo prefix)
        const nameHex = data.substring(offset, offset + nameLen * 2);
        offset += nameLen * 2;
        const withEnter = parseInt(data.substring(offset, offset + 2), 16);
        offset += 2;
        long.oathName = nameHex;  // Store in hex format to match credentials list
        long.withEnter = withEnter === 1;
      }
      
      setShortSlot(short);
      setLongSlot(long);
    } catch (e) {
      console.error('Failed to read PASS config:', e);
      enqueueSnackbar(`Failed to read PASS config: ${e}`, {variant: 'error'});
    }
  }, [dispatch, selectAdminApplet, enqueueSnackbar]);

  const writePassConfig = useCallback(async (slotIndex, config) => {
    try {
      if (config.type === SLOT_TYPE.OATH) {
        // OATH slots must be configured through OATH applet's SET_DEFAULT command
        if (!config.oathName) {
          throw new Error('OATH credential must be selected for OATH slot');
        }
        
        // Select OATH applet
        await selectOathApplet();
        
        // Extract the credential name (without type/algo prefix) from hex
        const nameBytes = hexStringToByte(config.oathName).slice(1);  // Skip type/algo byte
        const nameLen = nameBytes.length;
        
        // Build SET_DEFAULT command
        // Format: [OATH_TAG_NAME (0x71)] [name_len] [name...]
        let data = '71';  // OATH_TAG_NAME
        data += byteToHexString([nameLen]);
        data += byteToHexString(Array.from(nameBytes));
        
        const p1 = slotIndex === 'short' ? '01' : '02';
        const p2 = config.withEnter ? '01' : '00';
        const lc = byteToHexString([data.length / 2]);
        const capdu = `0055${p1}${p2}${lc}${data}`;
        
        let res = await dispatch(transceive(capdu, false, true));  // in_oath = true
        if (!res.endsWith("9000")) {
          const statusCode = res.slice(-4);
          if (statusCode === '6982') {
            throw new Error(
              'OATH applet authentication required. ' +
              'If you have set an OATH password, please go to the OATH page and verify it first. ' +
              'Or remove the OATH password on the OATH page (Config -> Set/Remove Password).'
            );
          }
          if (statusCode === '6985') {
            throw new Error('This credential is not compatible with PASS. Only HOTP credentials are supported, not TOTP.');
          }
          if (statusCode === '6984') {
            throw new Error('Credential not found. Please ensure the OATH credential exists.');
          }
          throw new Error(`Setting OATH default failed (status: ${statusCode})`);
        }
      } else {
        // OFF and STATIC types use Admin applet's WRITE_PASS_CONFIG command
        await selectAdminApplet();
        
        let data = byteToHexString([config.type]);
        
        if (config.type === SLOT_TYPE.STATIC) {
          if (!config.password) {
            throw new Error('Password cannot be empty for static slot');
          }
          if (config.password.length > 32) {
            throw new Error('Password too long (max 32 characters)');
          }
          
          const passwordBytes = [];
          for (let i = 0; i < config.password.length; i++) {
            passwordBytes.push(config.password.charCodeAt(i));
          }
          
          data += byteToHexString([config.password.length]);
          data += byteToHexString(passwordBytes);
          data += byteToHexString([config.withEnter ? 1 : 0]);
        }
        
        const p1 = slotIndex === 'short' ? '01' : '02';
        const lc = byteToHexString([data.length / 2]);
        const capdu = `0044${p1}00${lc}${data}`;
        
        let res = await dispatch(transceive(capdu));
        if (!res.endsWith("9000")) {
          const statusCode = res.slice(-4);
          if (statusCode === '6982') {
            // 认证状态丢失，清除前端状态，触发 App.js 的自动认证对话框
            dispatch(setAdminAuthenticated(false));
            throw new Error('Admin authentication expired. Please re-authenticate (dialog will appear automatically)');
          }
          throw new Error(`Writing PASS config failed (status: ${statusCode})`);
        }
      }
      
      await readPassConfig();
      enqueueSnackbar(`${slotIndex === 'short' ? 'Short press' : 'Long press'} slot configured successfully`, {variant: 'success'});
    } catch (e) {
      enqueueSnackbar(`Failed to configure slot: ${e}`, {variant: 'error'});
    }
  }, [dispatch, selectAdminApplet, selectOathApplet, readPassConfig, enqueueSnackbar]);

  const resetPassApplet = useCallback(async () => {
    if (!window.confirm('Are you sure you want to reset PASS applet? This will clear all password slots.')) {
      return;
    }
    
    try {
      await selectAdminApplet();
      let res = await dispatch(transceive("0013000000"));
      if (!res.endsWith("9000")) {
        const statusCode = res.slice(-4);
        if (statusCode === '6982') {
          // 认证状态丢失，清除前端状态，触发 App.js 的自动认证对话框
          dispatch(setAdminAuthenticated(false));
          throw new Error('Admin authentication expired. Please re-authenticate (dialog will appear automatically)');
        }
        throw new Error(`Resetting PASS applet failed (status: ${statusCode})`);
      }
      
      await readPassConfig();
      enqueueSnackbar('PASS applet reset successfully', {variant: 'success'});
    } catch (e) {
      enqueueSnackbar(`Failed to reset PASS applet: ${e}`, {variant: 'error'});
    }
  }, [dispatch, selectAdminApplet, readPassConfig, enqueueSnackbar]);

  const openConfigDialog = useCallback((slot) => {
    setCurrentSlot(slot);
    const config = slot === 'short' ? shortSlot : longSlot;
    setTempConfig({...config});
    setConfigDialogOpen(true);
  }, [shortSlot, longSlot]);

  const handleSaveConfig = useCallback(async () => {
    // Validate configuration before saving
    if (tempConfig.type === SLOT_TYPE.STATIC && !tempConfig.password) {
      enqueueSnackbar('Please enter a password for static slot', {variant: 'warning'});
      return;
    }
    if (tempConfig.type === SLOT_TYPE.OATH && !tempConfig.oathName) {
      enqueueSnackbar('Please select an OATH credential', {variant: 'warning'});
      return;
    }
    
    // 简单检查前端认证状态，不要主动调用 verifyAdminAuth
    // 让固件端的 APDU 命令自己判断，如果未认证会返回 6982
    if (!adminAuthenticated) {
      enqueueSnackbar('Admin authentication required. Please authenticate first', {variant: 'warning'});
      setConfigDialogOpen(false);
      return;
    }
    
    setConfigDialogOpen(false);
    await writePassConfig(currentSlot, tempConfig);
  }, [currentSlot, tempConfig, writePassConfig, enqueueSnackbar, adminAuthenticated]);

  useEffect(() => {
    if (device && adminAuthenticated) {
      // Add delay and execute sequentially to avoid device busy errors
      const loadData = async () => {
        try {
          // Wait a bit for device to be ready (especially when switching from other pages)
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Execute sequentially to avoid conflicts
          await readPassConfig();
          await new Promise(resolve => setTimeout(resolve, 100));
          await loadOathCredentials();
        } catch (e) {
          console.error('Failed to load Pass page data:', e);
        }
      };
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, adminAuthenticated]); // 监听 device 和 adminAuthenticated 变化

  const renderSlotCard = (slot, slotData, slotName) => {
    const isShort = slot === 'short';
    const statusColor = slotData.type === SLOT_TYPE.OFF ? 'default' : 'primary';
    
    return (
      <Card className={`${classes.slotCard} ${isShort ? classes.shortPressCard : classes.longPressCard}`}>
        <CardContent>
          <div className={classes.slotHeader}>
            <TouchAppIcon className={classes.slotIcon} color={isShort ? 'primary' : 'secondary'} fontSize="large" />
            <Typography variant="h5">
              {isShort ? 'Short Press' : 'Long Press'} Slot
            </Typography>
            <Chip 
              label={SLOT_TYPE_NAMES[slotData.type]} 
              color={statusColor}
              className={classes.statusChip}
              size="small"
            />
          </div>
          
          <Divider />
          
          <Box className={classes.configSection}>
            {slotData.type === SLOT_TYPE.OFF && (
              <Typography color="textSecondary">
                This slot is currently disabled
              </Typography>
            )}
            
            {slotData.type === SLOT_TYPE.STATIC && (
              <Box>
                <Typography variant="body2" color="textSecondary">
                  <LockIcon fontSize="small" style={{verticalAlign: 'middle'}} /> Static Password
                </Typography>
                <Typography variant="body2">
                  Password: ••••••••
                </Typography>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={slotData.withEnter} 
                      color="primary"
                      disableRipple
                      style={{pointerEvents: 'none'}}
                    />
                  }
                  label="Auto Enter"
                  style={{cursor: 'default'}}
                />
              </Box>
            )}
            
            {slotData.type === SLOT_TYPE.OATH && (
              <Box>
                <Typography variant="body2" color="textSecondary">
                  <VpnKeyIcon fontSize="small" style={{verticalAlign: 'middle'}} /> OATH Dynamic Password
                </Typography>
                <Typography variant="body2">
                  Credential: {slotData.oathName ? 
                    new TextDecoder("utf-8").decode(hexStringToByte(slotData.oathName).slice(1)) 
                    : 'Unknown'}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={slotData.withEnter} 
                      color="primary"
                      disableRipple
                      style={{pointerEvents: 'none'}}
                    />
                  }
                  label="Auto Enter"
                  style={{cursor: 'default'}}
                />
              </Box>
            )}
          </Box>
        </CardContent>
        <CardActions>
          <Button size="small" variant="outlined" color="primary" onClick={() => openConfigDialog(slot)}>
            Configure
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={1} justifyContent={'center'} className={classes.grid}>
        <Grid item xs={10}>
          <Card>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                PASS - Password Manager
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Configure touch button to automatically type passwords. Two slots are available: short press and long press.
              </Typography>
              
              {!adminAuthenticated && (
                <Box mt={2} mb={2} p={2} bgcolor="warning.light" borderRadius={4}>
                  <Typography variant="body1" color="textSecondary">
                    ⏳ Waiting for Admin authentication...
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Please enter your Admin PIN to view and configure PASS slots.
                  </Typography>
                </Box>
              )}
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {renderSlotCard('short', shortSlot, 'Short Press')}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderSlotCard('long', longSlot, 'Long Press')}
                </Grid>
              </Grid>
              
              <Box mt={2}>
                <Button onClick={readPassConfig} variant="outlined" color="primary" style={{marginRight: 8}}>
                  Refresh
                </Button>
                <Button onClick={resetPassApplet} variant="outlined" color="secondary">
                  Reset PASS
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Configure Dialog */}
      <Dialog 
        open={configDialogOpen} 
        onClose={() => setConfigDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableEnforceFocus
      >
        <form onSubmit={(e) => { e.preventDefault(); handleSaveConfig(); }}>
          <DialogTitle>
            Configure {currentSlot === 'short' ? 'Short Press' : 'Long Press'} Slot
          </DialogTitle>
          <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Slot Type</InputLabel>
            <Select
              value={tempConfig.type}
              onChange={(e) => setTempConfig({...tempConfig, type: e.target.value})}
            >
              <MenuItem value={SLOT_TYPE.OFF}>Disabled</MenuItem>
              <MenuItem value={SLOT_TYPE.STATIC}>Static Password</MenuItem>
              <MenuItem value={SLOT_TYPE.OATH}>OATH (Dynamic OTP)</MenuItem>
            </Select>
          </FormControl>
          
          {tempConfig.type === SLOT_TYPE.STATIC && (
            <TextField
              fullWidth
              margin="normal"
              label="Password"
              type="password"
              value={tempConfig.password}
              onChange={(e) => setTempConfig({...tempConfig, password: e.target.value})}
              helperText="Max 32 characters"
              inputProps={{maxLength: 32}}
              autoComplete="new-password"
            />
          )}
          
          {tempConfig.type === SLOT_TYPE.OATH && (
            <>
              <Box display="flex" alignItems="flex-end" gap={1}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>OATH Credential</InputLabel>
                  <Select
                    value={tempConfig.oathName}
                    onChange={(e) => setTempConfig({...tempConfig, oathName: e.target.value})}
                    disabled={oathCredentials.length === 0}
                  >
                    {oathCredentials.map((cred, index) => (
                      <MenuItem key={index} value={cred.fullName}>{cred.displayName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button 
                  onClick={loadOathCredentials}
                  color="primary"
                  style={{marginBottom: '8px'}}
                >
                  Refresh
                </Button>
              </Box>
              {oathCredentials.length === 0 && (
                <Typography color="error" variant="body2" style={{marginTop: '8px'}}>
                  ⚠️ No compatible OATH credentials found. 
                  <br />
                  <strong>Note:</strong> PASS applet only supports <strong>HOTP</strong> credentials, not TOTP. 
                  <br />
                  Please add an HOTP credential on the OATH page, then click Refresh.
                </Typography>
              )}
            </>
          )}
          
          {tempConfig.type !== SLOT_TYPE.OFF && (
            <FormControlLabel
              control={
                <Switch
                  checked={tempConfig.withEnter}
                  onChange={(e) => setTempConfig({...tempConfig, withEnter: e.target.checked})}
                />
              }
              label="Automatically press Enter after typing"
            />
          )}
          
          <DialogContentText style={{marginTop: 16}}>
            {tempConfig.type === SLOT_TYPE.STATIC && 
              'Static password will be typed when you press the touch button.'}
            {tempConfig.type === SLOT_TYPE.OATH && 
              'A dynamic OTP code will be generated and typed when you press the touch button.'}
            {tempConfig.type === SLOT_TYPE.OFF && 
              'This slot will be disabled and nothing will happen when pressed.'}
          </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}

